import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { FinanceApiService } from '../finance/finance-api.service';
import { CustomerCreditLimitRecord, SalesApiService } from './sales-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-customer-credit-limits',
  imports: [AinPageComponent, AinStateComponent, FormsModule, TableModule, ButtonModule, CustomerSelectorComponent],
  template: `
    <ain-page [title]="t('sales.credit.limits.title')" [description]="t('sales.credit.limits.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('sales.action.create')" (click)="openCreate()"></button>
            <button pButton type="button" [label]="t('sales.action.edit')" severity="secondary" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
            <button pButton type="button" [label]="t('sales.action.refresh')" severity="secondary" (click)="load()"></button>
          </div>
          <div class="filters">
            <label><span>{{ t('sales.orders.fields.customer') }}</span><ain-customer-selector [(ngModel)]="filterCustomerId" (ngModelChange)="load()" /></label>
          </div>
          <p-table [value]="rows()" dataKey="id" [paginator]="true" [rows]="15" [selection]="selectedRows()" (selectionChange)="onSelectionChange($event)">
            <ng-template pTemplate="header">
              <tr>
                <th style="width: 44px"><p-tableHeaderCheckbox /></th>
                <th>{{ t('sales.orders.fields.customer') }}</th>
                <th>{{ t('sales.credit.fields.limit') }}</th>
                <th>{{ t('sales.credit.fields.currency') }}</th>
                <th>{{ t('columns.status') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><p-tableCheckbox [value]="row" /></td>
                <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ customerLabel(row.customerAccountId) }}</button></td>
                <td>{{ row.creditLimit }}</td>
                <td>{{ row.currencyCode }}</td>
                <td>{{ row.isActive ? t('sales.credit.active') : t('sales.credit.inactive') }}</td>
              </tr>
            </ng-template>
          </p-table>
          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .filters label { display: grid; gap: 6px; max-width: 320px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .error { margin: 0; color: #b91c1c; }
  `]
})
export class SalesCustomerCreditLimitsComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly financeApi = inject(FinanceApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly rows = signal<CustomerCreditLimitRecord[]>([]);
  readonly selectedRows = signal<CustomerCreditLimitRecord[]>([]);
  readonly customers = signal<Map<string, string>>(new Map());
  filterCustomerId: string | null = null;

  ngOnInit(): void {
    this.financeApi.listPlatformCustomers({ take: 500 }).subscribe({
      next: response => {
        const map = new Map<string, string>();
        for (const item of response.items ?? []) {
          map.set(item.id, `${item.displayName} #${item.customerCode}`);
        }
        this.customers.set(map);
      },
      error: () => this.customers.set(new Map())
    });
    this.load();
  }

  customerLabel(customerId: string): string {
    return this.customers().get(customerId) ?? customerId.slice(0, 8);
  }

  load(): void {
    this.loading.set(true);
    this.error.set('');
    this.api.listCustomerCreditLimits(this.filterCustomerId ?? undefined).subscribe({
      next: limits => { this.rows.set(limits); this.loading.set(false); },
      error: () => { this.rows.set([]); this.error.set(this.t('sales.credit.limits.errorLoad')); this.loading.set(false); }
    });
  }

  onSelectionChange(selection: CustomerCreditLimitRecord[] | CustomerCreditLimitRecord | null): void {
    this.selectedRows.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  openCreate(): void { void this.router.navigate(['/sales/customer-credit-limits/new']); }
  openSelected(): void { const row = this.selectedRows()[0]; if (row) this.openDetail(row.id); }
  openDetail(id: string): void { void this.router.navigate(['/sales/customer-credit-limits', id]); }
  t(key: string): string { return this.localization.translate(key); }
}
